import type { Subtitle } from 'app/types/graphql';
import { ForwardIcon, PauseIcon, PlayIcon, PlayOnThumbnailIcon, RewindIcon } from 'app/ui/components/atoms/controls';
import Typography from 'app/ui/components/atoms/typography';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';

import SubtitlesOffIcon from 'app/assets/subtitles-off.svg';
import SubtitlesOnIcon from 'app/assets/subtitles-on.svg';
import { StyledSubtitles } from 'app/ui/components/atoms/styled-subtitles';
import useConfig from 'app/config';

const VideoContainer = styled.div<{ $isCentered?: boolean }>`
  position: relative;
  &:hover button {
    opacity: 1;
  }
  ${({ $isCentered }) => $isCentered && 'align-self: center;'}
`;

const VideoElement = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 0.5rem;
`;

const Button = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.white};
  opacity: 0;
  transition: opacity 0.3s ease;
  &:focus {
    outline: none;
  }
`;

const PlayButton = styled(Button)`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  gap: 0.66rem;
`;

const ControlWrapper = styled.div<{ showPointer: boolean }>`
  display: flex;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  ${({ showPointer }) => (showPointer ? 'cursor: pointer' : null)};
`;

const Controls = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 3rem;
`;

const RewindButton = styled(Button)`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  gap: 0.66rem;
`;

const ForwardButton = styled(Button)`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  gap: 0.66rem;
`;

const ProgressBarWrapper = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  background: ${({ theme }) => `${theme.colors.white}33`};
  height: 0.3125rem;
`;

const ProgressBar = styled.div<{ progress: number }>`
  width: ${({ progress }) => `${progress}%`};
  height: 100%;
  background: ${({ theme }) => theme.colors.white};
`;

export const ThumbnailOverlay = styled.div`
  height: 100%;
  position: absolute;
  z-index: 2;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 1.5625rem;
  bottom: 1.75rem;
  left: 1.5rem;
  right: 1.5rem;
  border-radius: 0.5rem;
  cursor: pointer;
`;

const DurationAndPlayWrapper = styled.div`
  display: flex;
  flex-direction: column;
  text-align: end;
  gap: 1.5rem;
  align-items: flex-end;
`;

const Title = styled(Typography)`
  color: ${({ theme }) => theme.colors.white};
  z-index: 3;
  align-self: flex-end;
  display: flex;
`;

const Duration = styled(Typography)`
  color: ${({ theme }) => theme.colors.white};
  align-self: flex-end;
  z-index: 3;
`;

export const PlayIconWrapper = styled.div`
  position: relative;
  right: 0;
  z-index: 3;
  cursor: pointer;
`;

const SubtitleToggleWrapper = styled.div`
  position: absolute;
  bottom: ${({ theme: { spacing } }) => spacing(4)};
  right: ${({ theme: { spacing } }) => spacing(4)};
  z-index: 5;
`;

const SubtitleButton = styled(Button)`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: ${({ theme: { spacing } }) => spacing(6)};
    height: ${({ theme: { spacing } }) => spacing(6)};
  }
`;

export interface VideoPlayerProps {
  src: string;
  title: string;
  duration?: string | null;
  thumbnail?: string;
  loop?: boolean;
  showRewindAndForward?: boolean;
  showProgress?: boolean;
  className?: string;
  autoPlay?: boolean;
  trackEventOnClickPlay?: () => void;
  subtitle?: Subtitle | null;
  showSubtitles?: boolean;
  isCentered?: boolean;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({
  src,
  thumbnail,
  title,
  duration,
  showRewindAndForward = false,
  showProgress = false,
  className,
  loop,
  autoPlay,
  trackEventOnClickPlay,
  subtitle,
  showSubtitles = true,
  isCentered,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const theme = useTheme();
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [progress, setProgress] = useState(0);
  const [showThumbnail, setShowThumbnail] = useState(!autoPlay);
  const [subtitlesEnabled, setSubtitlesEnabled] = useState(showSubtitles);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const { config } = useConfig();

  const subtitlesGloballyEnabled = config.REACT_APP_SUBTITLES_ENABLED;

  const togglePlay = async () => {
    if (videoRef.current) {
      if (isPlaying) {
        await videoRef.current.pause();
      } else {
        try {
          await videoRef.current.play();
        } catch (error) {
          console.error('Error playing video:', error);
        }
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const currentProgress = (videoRef.current.currentTime / videoRef.current.duration) * 100;
      setProgress(currentProgress);
    }
  };

  const handlePlay = async () => {
    setShowThumbnail(false);
    setIsVideoLoaded(true);
    setIsPlaying(true);
    if (videoRef.current) {
      try {
        await videoRef.current.play();
      } catch (error) {
        console.error('Error playing video:', error);
        // Still show controls even if play fails
        setIsVideoLoaded(true);
      }
    }
    if (trackEventOnClickPlay) {
      trackEventOnClickPlay();
    }
  };

  const rewind = () => {
    if (videoRef.current) {
      videoRef.current.currentTime -= 10;
    }
  };

  const forward = () => {
    if (videoRef.current) {
      videoRef.current.currentTime += 10;
    }
  };

  const toggleSubtitles = () => {
    const newState = !subtitlesEnabled;
    setSubtitlesEnabled(newState);

    if (videoRef.current && videoRef.current.textTracks.length > 0) {
      videoRef.current.textTracks[0].mode = newState ? 'showing' : 'disabled';
    }
  };

  useEffect(() => {
    const setInitialSubtitleState = () => {
      if (videoRef.current && videoRef.current.textTracks.length > 0) {
        videoRef.current.textTracks[0].mode = subtitlesEnabled ? 'showing' : 'disabled';
      }
    };

    const handleLoadedMetadata = () => {
      setInitialSubtitleState();
      setIsVideoLoaded(true);
    };

    const videoElement = videoRef.current;
    if (videoElement) {
      if (videoElement.readyState >= 2) {
        setInitialSubtitleState();
        setIsVideoLoaded(true);
      } else {
        videoElement.addEventListener('loadedmetadata', handleLoadedMetadata);
      }
    }

    return () => {
      if (videoElement) {
        videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
      }
    };
  }, [subtitlesEnabled]);

  return (
    <VideoContainer $isCentered={isCentered}>
      {showThumbnail && (
        <ThumbnailOverlay onClick={handlePlay}>
          <Title variant="h5" color={theme.colors.white}>
            {title}
          </Title>
          <DurationAndPlayWrapper>
            <PlayIconWrapper onClick={handlePlay}>
              <PlayOnThumbnailIcon />
            </PlayIconWrapper>
            {duration && (
              <Duration variant="h5" color={theme.colors.white}>
                {duration}
              </Duration>
            )}
          </DurationAndPlayWrapper>
        </ThumbnailOverlay>
      )}
      <VideoElement
        ref={videoRef}
        controls={false}
        onTimeUpdate={handleTimeUpdate}
        onEnded={() => setIsPlaying(false)}
        onLoadedMetadata={() => setIsVideoLoaded(true)}
        className={className}
        loop={loop}
        key={src}
        poster={thumbnail}
        autoPlay={autoPlay}
        crossOrigin="anonymous"
      >
        <source src={src} type="video/mp4" />
        Your browser does not support the video tag.
      </VideoElement>
      {subtitle && subtitlesEnabled && subtitlesGloballyEnabled && (
        <StyledSubtitles videoRef={videoRef} subtitlesSrc={subtitle.signedUrl} />
      )}
      {(progress > 0 || showRewindAndForward || isVideoLoaded) && (
        <ControlWrapper {...(!showRewindAndForward && { onClick: togglePlay })} showPointer={!showRewindAndForward}>
          <Controls>
            {showRewindAndForward && (
              <RewindButton onClick={rewind}>
                <RewindIcon />
                <Typography variant="caption">10 Sec</Typography>
              </RewindButton>
            )}
            <PlayButton onClick={togglePlay}>
              {isPlaying ? <PauseIcon /> : <PlayIcon />}
              <Typography variant="caption">{isPlaying ? 'Pause' : 'Play'}</Typography>
            </PlayButton>
            {showRewindAndForward && (
              <ForwardButton onClick={forward}>
                <ForwardIcon />
                <Typography variant="caption">10 Sec</Typography>
              </ForwardButton>
            )}
          </Controls>
        </ControlWrapper>
      )}
      {showProgress && (
        <ProgressBarWrapper>
          <ProgressBar progress={progress} />
        </ProgressBarWrapper>
      )}
      {subtitle && subtitlesGloballyEnabled && progress > 0 && (
        <SubtitleToggleWrapper>
          <SubtitleButton onClick={toggleSubtitles}>
            <img
              src={subtitlesEnabled ? SubtitlesOnIcon : SubtitlesOffIcon}
              alt={subtitlesEnabled ? 'Subtitles On' : 'Subtitles Off'}
            />
          </SubtitleButton>
        </SubtitleToggleWrapper>
      )}
    </VideoContainer>
  );
};

export default VideoPlayer;
